/**
 * TT-02 — narrow domain types for the standby / time-tracking surface, sourced
 * from the generated `time-tracking/v1` contract types (`@aeriosiq/types`,
 * `TimeTracking*` exports). Re-exported here so the feature's hooks and
 * components import from one local module rather than reaching into the codegen
 * surface directly (mirrors the support + tenancy features).
 *
 * NEVER hand-edit the codegen — regenerate with
 * `pnpm contracts:fetch && pnpm types:generate`.
 */
import type { TimeTrackingComponents } from "@aeriosiq/types";

type Schemas = TimeTrackingComponents["schemas"];

/** One standby / time tracking, anchor already resolved to display labels. */
export type TimeTracking = Schemas["TimeTracking"];
/** A page of trackings + the totals over the WHOLE filtered set. */
export type TimeTrackingListResponse = Schemas["TimeTrackingListResponse"];
/** Minute totals over the filtered set (not the page). */
export type TimeTrackingTotals = Schemas["TimeTrackingTotals"];
/** POST body — the pilot create form. */
export type TimeTrackingCreateRequest = Schemas["TimeTrackingCreateRequest"];
/** PATCH body — partial edit of an unlocked tracking. */
export type TimeTrackingPatchRequest = Schemas["TimeTrackingPatchRequest"];
/** POST /trackings/lock body. */
export type TimeTrackingLockRequest = Schemas["TimeTrackingLockRequest"];
/** POST /trackings/lock response — counts + the resulting locked set. */
export type TimeTrackingLockResponse = Schemas["TimeTrackingLockResponse"];
/** Per-project commercial config. */
export type TimeTrackingConfig = Schemas["TimeTrackingConfig"];
/** PUT body for the config card. */
export type TimeTrackingConfigPutRequest =
  Schemas["TimeTrackingConfigPutRequest"];
/** One assignment of the caller, pre-resolved into an anchor chain. */
export type MyTimeTrackingContextItem = Schemas["MyTimeTrackingContextItem"];
/** GET /my-context response. */
export type MyTimeTrackingContextResponse =
  Schemas["MyTimeTrackingContextResponse"];

/** The anchor level a tracking is booked against. */
export type TimeTrackingEntityType = TimeTracking["entityType"];
/** Every activity type a READ may return. */
export type TimeTrackingActivityType = TimeTracking["activityType"];
/** The two a v1 client may WRITE. */
export type StandbyType = TimeTrackingCreateRequest["activityType"];

/**
 * The two standby flavours, in the order the form shows them.
 *
 * Declared here rather than derived from the codegen union so the segmented
 * control has a stable ORDER (a TS union has none) and so the labels live next
 * to the values they label.
 */
export const STANDBY_TYPES = [
  "standby_weather",
  "standby_technical",
] as const satisfies readonly StandbyType[];

/** Anchor levels the pilot chips can produce (design §3.4 / contract TT-D3). */
export const ANCHOR_LEVELS = ["turbine", "park", "project"] as const;
export type AnchorLevel = (typeof ANCHOR_LEVELS)[number];

/**
 * Chip → wire mapping. The pilot never sees `entityType`; he sees places.
 * "This turbine" is the CONTRACT POSITION, not the inspection instruction —
 * turbine bookings anchor at the level invoices aggregate on (Q-TT-2).
 */
export const ENTITY_TYPE_BY_ANCHOR_LEVEL: Record<
  AnchorLevel,
  TimeTrackingEntityType
> = {
  turbine: "contract_position",
  park: "contract",
  project: "project",
};

/** Filter state for the list, mirrored into the URL (house pattern). */
export interface TimeTrackingListFilters {
  /** Inclusive lower bound on startTime (ISO). */
  from?: string;
  /** Exclusive upper bound on startTime (ISO). */
  to?: string;
  activityType?: TimeTrackingActivityType;
  locked?: boolean;
  userId?: string;
  projectId?: string;
  entityType?: TimeTrackingEntityType;
  entityId?: string;
  page?: number;
  pageSize?: number;
  /**
   * TT-05 — `own` (default) reads this tenant's own schema; `member` reads
   * trackings on OTHER tenants' projects we have been granted access to.
   *
   * They are separate calls by design, not two pages of one list: a
   * subcontractor pilot's tracking is stored in the PROJECT OWNER's schema, so
   * the two sets come from different schemas under different rules. The server
   * does not merge them — `useMyTimeTrackingsQuery` does.
   *
   * `member` is un-paginated (the grant set bounds it), so `page`/`pageSize`
   * are ignored on that call.
   */
  scope?: "own" | "member";
}
